// Copyright (c) Ping. All rights reserved.
//
// invite-flow Wave-2C — thin web host. Routes the router-driven
// `status` modal slot through the shared `SetStatusModal`
// (NativeWind boundary in `packages/ui/src/status/`). The status
// persistence callback lands in a follow-up slice — the host
// currently no-ops on commit / clear and closes the overlay through
// the router.

import { SetStatusModal, type UserStatus } from '@ping/ui';

import { log } from '@core/log/log';
import { updateProfile } from '@features/settings/web/profilesApi';
import { useRouter } from '@shell/RouterContext';

/** The browser's IANA timezone id (so peers can render the user's local time). */
function browserTimezone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
}

/**
 * Mounts `<SetStatusModal />` when the router's modal slot is set to `status`.
 * Commit/clear persist the status to the server (`PATCH /v1/me/profile`) so it
 * fans out to peers and surfaces in their profile popover/modal. Best-effort:
 * a persistence failure never blocks closing the overlay.
 */
export function SetStatusModalHost(): JSX.Element {
  const router = useRouter();
  const visible = router.modal.kind === 'status';

  const persist = (body: Parameters<typeof updateProfile>[0]): void => {
    void updateProfile(body).catch((err) => {
      log.debug({ event: 'status.persist.failed', err: (err as Error).message });
    });
  };

  return (
    <SetStatusModal
      visible={visible}
      onCommit={(status: UserStatus) => {
        router.closeModal();
        const tz = browserTimezone();
        persist({
          statusEmoji: status.emoji ?? '',
          statusText: status.text ?? '',
          statusExpiresAt: status.expiresAt !== undefined ? status.expiresAt.getTime() : 0,
          ...(tz !== undefined ? { timezone: tz } : {}),
        });
      }}
      onClear={() => {
        router.closeModal();
        // Clearing the text clears the whole status server-side.
        persist({ statusText: '' });
      }}
      onClose={() => {
        router.closeModal();
      }}
    />
  );
}
